import React, { useEffect, useState } from "react";

import {
  calculateFootprintingProgress,
  formatNumericCurrency,
  getContactLink,
  goTo,
  isPercentageComplete,
  queryIsLoading,
  useGetFootprintingCropFootprints,
  useGetFootprintingDairyFootprints,
  useGetFootprintingFarmFootprint,
  useNavigate,
  useTranslation,
} from "utils";

import { CarbonProgram } from "models";

import { Button, Card, Col, Drawer, Flex, Icon, Link, Row, Skeleton, Tooltip, Typography } from "components";

import FootprintingOnboardingSteps from "pages/app/_app/components/FootprintingOnboardingSteps";
import FootprintingSubmissionModal from "pages/app/footprinting/components/FootprintingSubmissionModal";

interface FootprintingProgressWidgetProps {
  farmId: string;
  year: number;
  carbonProgram?: CarbonProgram;
}

const FootprintingProgressWidget = ({ farmId, year, carbonProgram }: FootprintingProgressWidgetProps) => {
  const { t } = useTranslation("footprinting");
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(true);
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [onboardingDrawerVisable, setOnboardingDrawerVisable] = useState(false);

  const farmProgressQuery = useGetFootprintingFarmFootprint(farmId, year);
  const cropProgressQueries = useGetFootprintingCropFootprints(farmId, [year], carbonProgram?.farmLevelFootprinting?.programCrops);
  const dairyProgressQueries = useGetFootprintingDairyFootprints(farmId, [year]);
  const showPayouts = carbonProgram?.platformFeatures?.footprinting?.payouts ?? true;

  const { totalPayout, isSubmitted } = calculateFootprintingProgress(farmProgressQuery.data, [
    ...cropProgressQueries.filter((cpq) => !!cpq.data).map((cpq) => cpq.data),
    ...dairyProgressQueries.filter((dpq) => !!dpq.data).map((dpq) => dpq.data),
  ]);

  useEffect(() => {
    setLoading([...cropProgressQueries, farmProgressQuery].some(queryIsLoading));
  }, [farmProgressQuery, cropProgressQueries]);

  if (loading) {
    return (
      <Card className="padding-125 width-100 margin-top-100 footprint-progress-widget">
        <Flex className="skeleton" vertical={true}>
          <div className="skeleton-containers">
            <Skeleton type="block" width="8.875rem" height="1.125rem" className="skeleton-rectangular" />
          </div>
          <div className="skeleton-containers margin-bottom-150">
            <Skeleton type="block" height="11.875rem" className="skeleton-rectangular" />
          </div>
          <div className="skeleton-containers">
            <Skeleton type="block" width="16.5rem" height="0.625rem" className="skeleton-rectangular" />
          </div>
          <div className="skeleton-containers margin-bottom-50">
            <Skeleton type="block" width="8.875rem" height="2.375rem" className="skeleton-rectangular" />
          </div>
          <Row justify="space-between" className="padding-0">
            <Col sm={12} className="skeleton-containers">
              <Skeleton type="block" width="8.875rem" height="1.5rem" className="skeleton-rectangular" />
            </Col>
            <Col sm={3} className="skeleton-containers padding-0">
              <Skeleton type="block" width="2.125rem" height="0.875rem" className="skeleton-rectangular" />
            </Col>
          </Row>
          <div className="skeleton-containers">
            <Skeleton type="block" height="0.625rem" className="skeleton-rectangular" />
          </div>
          <div className="skeleton-containers">
            <Skeleton type="block" height="3rem" className="skeleton-rectangular" />
          </div>
          <Flex className="skeleton-containers">
            <Skeleton type="block" width="13.625rem" height="1.25rem" className="skeleton-rectangular centered" />
          </Flex>
        </Flex>
      </Card>
    );
  }

  const readyToSubmit =
    isPercentageComplete(farmProgressQuery.data?.completionPct ?? 0) &&
    [...cropProgressQueries, ...dairyProgressQueries].some((cpq) => isPercentageComplete(cpq.data?.completionPct ?? 0));

  const handleSubmitClick = () => {
    setShowSubmitModal(true);
  };

  const handleModalConfirm = () => {
    setShowSubmitModal(false);
    navigate(`review/${year}`);
  };

  const handleModalCancel = () => {
    setShowSubmitModal(false);
  };

  const handleOnboardingBegin = () => {
    setOnboardingDrawerVisable(false);
  };

  return (
    <>
      <Flex className="width-100 flex footprint-progress-widget container" vertical>
        <Flex className="padding-100 footprinting-sidebar-container" vertical>
          <Flex align="center" justify="center" className="flex-1">
            <Card className="margin-top-auto flex-center">
              <Flex align="center" justify="center" vertical gap={8}>
                <Icon size={72} iconName="klim/crop-footprint-blue" className="margin-bottom-100 margin-top-100" />
                <Typography variant="paragraph-l" className="centered-text">
                  {t("progressWidget.understand")}
                </Typography>
                <Typography variant="paragraph-m" className="margin-bottom-200 centered-text">
                  {t("progressWidget.title")}
                </Typography>
              </Flex>
              <hr className="dashed-hr" />
              <Flex vertical gap={12}>
                <Flex justify="flex-start" align="flex-start" className="margin-top-150">
                  <Typography variant="paragraph-s" className="step-number">
                    1
                  </Typography>
                  <Typography variant="paragraph-m" className="margin-left-50">
                    {t("progressWidget.bullet.collect")}
                  </Typography>
                </Flex>
                <Flex justify="flex-start" align="flex-start">
                  <Typography variant="paragraph-s" className="step-number">
                    2
                  </Typography>
                  <Typography variant="paragraph-m" className="margin-left-50">
                    {t("progressWidget.bullet.complete")}
                  </Typography>
                </Flex>
                <Flex justify="flex-start" align="flex-start">
                  <Typography variant="paragraph-s" className="step-number">
                    3
                  </Typography>
                  <Typography variant="paragraph-m" className="margin-left-50">
                    {t("progressWidget.bullet.results")}
                  </Typography>
                </Flex>
                <Flex justify="flex-start" align="flex-start">
                  <Typography variant="paragraph-s" className="step-number">
                    4
                  </Typography>
                  <Typography variant="paragraph-m" className="margin-left-50">
                    {t("progressWidget.bullet.review")}
                  </Typography>
                </Flex>
                {showPayouts && (
                  <Flex justify="flex-start" align="flex-start">
                    <Typography variant="paragraph-s" className="step-number">
                      5
                    </Typography>
                    <Typography variant="paragraph-m" className="margin-left-50">
                      {t("progressWidget.bullet.verified")}
                    </Typography>
                  </Flex>
                )}
                <Flex justify="flex-start" align="center" className="margin-left-150 margin-bottom-150">
                  <Link className="margin-left-25" onClick={() => setOnboardingDrawerVisable(true)}>
                    {t("progressWidget.learnMore")}
                  </Link>
                </Flex>
              </Flex>
            </Card>
          </Flex>
        </Flex>
        <hr className="margin-0 dashed-hr margin-left-100 margin-right-100" />
        <div className="padding-left-100 padding-bottom-100 padding-right-100 padding-top-200">
          {showPayouts && (
            <Flex className="margin-bottom-100" justify="space-between" align="center">
              <Typography variant="paragraph-s" color="grey-600">
                {t("progressWidget.payout")}
              </Typography>
              <Typography variant="h4" color="green-400">
                {t("progressWidget.upto")} {formatNumericCurrency(totalPayout, { currency: carbonProgram?.currency })}
              </Typography>
            </Flex>
          )}
          {isSubmitted ? (
            <div className="margin-bottom-100 padding-100  submitted-payment">
              <Flex align="center">
                <Icon iconName="tabler/circle-check-filled" className="margin-right-50 green-text" />
                <Typography variant="h6">{t("progressWidget.wellDone")}</Typography>
              </Flex>
              <Typography variant="paragraph-s" className="margin-top-100 margin-bottom-100">
                {carbonProgram?.platformFeatures.footprinting?.payouts === false ? t("progressWidget.noPayoutSubmitted") : t("progressWidget.payoutSubmitted")}
              </Typography>
              <Flex>
                <Typography variant="paragraph-m">
                  {t("reviewAndSubmit.support")}
                  <Link className="margin-left-25" onClick={(e) => goTo(e, null, getContactLink(), true)}>
                    {t("reviewAndSubmit.contact")}
                  </Link>
                </Typography>
              </Flex>
            </div>
          ) : (
            <div>
              {showPayouts ? (
                <Typography variant="paragraph-s" className="margin-top-100 margin-bottom-100">
                  {t("progressWidget.payoutProgress")}
                </Typography>
              ) : null}
              {!readyToSubmit ? (
                <Tooltip title={t("progressWidget.button.completion")} style={{ display: "flex", alignItems: "center" }}>
                  <Button type="primary" className=" margin-top-200" disabled={true} onClick={() => navigate(`review/${year}`)}>
                    {t("progressWidget.submitButton")}
                  </Button>
                </Tooltip>
              ) : (
                <Button type="primary" className="0 margin-top-200" disabled={false} onClick={handleSubmitClick}>
                  {t("progressWidget.submitButton")}
                </Button>
              )}
            </div>
          )}
        </div>
      </Flex>
      <FootprintingSubmissionModal carbonProgram={carbonProgram} open={showSubmitModal} onConfirm={handleModalConfirm} onCancel={handleModalCancel} />
      <Drawer
        data-testid="footprinting-onboarding-drawer"
        title={t("onboarding.howToAssessTitle")}
        visible={onboardingDrawerVisable}
        onClose={() => setOnboardingDrawerVisable(false)}
        destroyOnHidden
      >
        <FootprintingOnboardingSteps carbonProgram={carbonProgram} onBegin={handleOnboardingBegin} />
      </Drawer>
    </>
  );
};

export default FootprintingProgressWidget;
